/**
 * Featured Collection Home Page
 * Handles drag scroll, quick add functionality, and custom scrollbar
 * Note: Wishlist functionality is now handled by wishlist.js
 * Note: Form submission is handled by product-form.js Web Component
 */

/**
 * Product Form Home - Web Component for variant selection
 * Extends Shopify's native product-form Web Component
 */
function initProductFormHome() {
  if (!customElements.get('product-form-home')) {
    // Get the base ProductForm class
    const ProductForm = customElements.get('product-form');

    if (!ProductForm) {
      // product-form not ready yet, wait and retry
      setTimeout(initProductFormHome, 50);
      return;
    }

    customElements.define(
      'product-form-home',
      class ProductFormHome extends ProductForm {
      constructor() {
        super();

        // Ensure cart is properly detected (re-assign after super())
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Initialize after element is connected and rendered
        // Use requestAnimationFrame to ensure DOM is fully ready
        requestAnimationFrame(() => {
          this.initialize();
        });
      }

      /**
       * Initialize the component after DOM is ready
       */
      initialize() {
        // Get the product card and ID
        this.productCard = this.closest('[data-product-id]');
        this.productId = this.productCard?.dataset.productId;

        if (!this.form || !this.productId) {
          return;
        }

        // Load product variants data
        this.variantsData = this.loadProductData();

        if (!this.variantsData) {
          return;
        }

        // Initialize variant selection
        this.initVariantSelection();
      }

      /**
       * Load product variants from JSON script tag
       */
      loadProductData() {
        const dataScript = document.querySelector(`[data-product-json="${this.productId}"]`);
        if (!dataScript) return null;

        try {
          return JSON.parse(dataScript.textContent);
        } catch (error) {
          console.error('Error parsing product data:', error);
          return null;
        }
      }

      /**
       * Initialize variant selection event listeners
       */
      initVariantSelection() {
        // Listen to size option changes
        const sizeInputs = this.form.querySelectorAll('.product-card-home__size-input');

        sizeInputs.forEach(input => {
          input.addEventListener('change', () => {
            this.onVariantChange();
          });
        });
      }

      /**
       * Handle variant change
       */
      onVariantChange() {
        const selectedOptions = this.getSelectedOptions();
        const variant = this.getVariantFromOptions(selectedOptions);

        if (variant) {
          this.updateVariantInput(variant);
          this.updateSubmitButton(variant);
        }
      }

      /**
       * Get currently selected options
       */
      getSelectedOptions() {
        const options = [];

        // Get only size radio inputs (product options)
        const selectedInputs = this.form.querySelectorAll('.product-card-home__size-input:checked');

        selectedInputs.forEach(input => {
          if (input.value) {
            options.push(input.value);
          }
        });

        return options;
      }

      /**
       * Find variant matching selected options
       * @param {Array} options - Array of selected option values (e.g., ['3'] for size 3)
       */
      getVariantFromOptions(options) {
        return this.variantsData.find(variant => {
          // Match all selected option values against variant's option1, option2, option3
          return options.every(selectedValue => {
            return variant.option1 === selectedValue ||
                   variant.option2 === selectedValue ||
                   variant.option3 === selectedValue;
          });
        });
      }

      /**
       * Update hidden variant ID input
       */
      updateVariantInput(variant) {
        const variantInput = this.form.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
          variantInput.disabled = !variant.available;
        }
      }

      /**
       * Update submit button state
       */
      updateSubmitButton(variant) {
        const submitButton = this.form.querySelector('.product-card-home__quick-shop');
        if (!submitButton) return;

        const buttonText = submitButton.querySelector('span');

        if (variant.available) {
          submitButton.disabled = false;
          if (buttonText) {
            buttonText.textContent = 'AJOUTER AU PANIER';
          }
        } else {
          submitButton.disabled = true;
          if (buttonText) {
            buttonText.textContent = window.variantStrings?.soldOut || 'Épuisé';
          }
        }
      }
    }
    );
  }
}

// Initialize product-form-home when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductFormHome);
} else {
  initProductFormHome();
}

/**
 * Featured Collection Home Page - Main class
 * Handles drag scroll and custom scrollbar
 */
class FeaturedCollectionHomePage {
  constructor() {
    this.init();
  }

  init() {
    this.initDragScroll();
    this.initCustomScrollbar();
    this.initAutoScroll();
  }

  /**
   * Drag to scroll functionality
   */
  initDragScroll() {
    const scrollWrappers = document.querySelectorAll('.featured-collection-home__scroll-wrapper');

    scrollWrappers.forEach(wrapper => {
      let isDown = false;
      let startX;
      let scrollLeft;
      let hasMoved = false;

      const handleMouseDown = (e) => {
        // Allow interaction with buttons, inputs, labels in hover card
        if (e.target.closest('.product-card-home__hover-content button, .product-card-home__hover-content input, .product-card-home__hover-content label')) {
          return;
        }

        isDown = true;
        hasMoved = false;
        startX = e.pageX;
        scrollLeft = wrapper.scrollLeft;
        wrapper.style.cursor = 'grabbing';
      };

      const handleMouseLeave = () => {
        if (isDown) {
          isDown = false;
          hasMoved = false;
          wrapper.classList.remove('is-dragging');
          wrapper.style.cursor = 'grab';
        }
      };

      const handleMouseUp = () => {
        if (isDown) {
          isDown = false;
          wrapper.style.cursor = 'grab';

          // Small delay to allow click event to check hasMoved
          setTimeout(() => {
            wrapper.classList.remove('is-dragging');
            hasMoved = false;
          }, 50);
        }
      };

      const handleMouseMove = (e) => {
        if (!isDown) return;

        const x = e.pageX;
        const walk = (startX - x) * 1.5;

        // Only mark as dragging if moved more than 10px
        if (Math.abs(walk) > 10) {
          if (!hasMoved) {
            hasMoved = true;
            wrapper.classList.add('is-dragging');
          }
          e.preventDefault();
          wrapper.scrollLeft = scrollLeft + walk;
        }
      };

      const handleClick = (e) => {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };

      // Add event listeners
      wrapper.addEventListener('mousedown', handleMouseDown);
      wrapper.addEventListener('mouseleave', handleMouseLeave);
      wrapper.addEventListener('mouseup', handleMouseUp);
      wrapper.addEventListener('mousemove', handleMouseMove);

      // Use capture phase to intercept clicks before they reach links
      wrapper.addEventListener('click', handleClick, true);

      // Also prevent link navigation specifically
      const links = wrapper.querySelectorAll('a');
      links.forEach(link => {
        link.addEventListener('click', (e) => {
          if (hasMoved) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        });
      });

      // Global mouseup to handle release outside wrapper
      document.addEventListener('mouseup', handleMouseUp);

      // Set initial cursor
      wrapper.style.cursor = 'grab';
    });
  }

  /**
   * Custom scrollbar functionality
   */
  initCustomScrollbar() {
    const scrollWrappers = document.querySelectorAll('.featured-collection-home__scroll-wrapper');

    scrollWrappers.forEach(wrapper => {
      const customScrollbar = wrapper.parentElement.querySelector('.featured-collection-home__custom-scrollbar');
      const thumb = customScrollbar?.querySelector('.featured-collection-home__custom-scrollbar-thumb');

      if (!customScrollbar || !thumb) return;

      // Store reference for auto-scroll pause callback
      wrapper._scrollbarDragActive = false;
      wrapper._pauseAutoScroll = null; // Will be set by auto-scroll init

      const updateScrollbar = () => {
        const scrollWidth = wrapper.scrollWidth;
        const clientWidth = wrapper.clientWidth;
        const scrollLeft = wrapper.scrollLeft;

        // Hide scrollbar if content fits
        if (scrollWidth <= clientWidth) {
          customScrollbar.style.display = 'none';
          return;
        } else {
          customScrollbar.style.display = 'block';
        }

        // Calculate thumb width as percentage
        const thumbWidth = (clientWidth / scrollWidth) * 100;
        const thumbPosition = (scrollLeft / scrollWidth) * 100;

        thumb.style.width = thumbWidth + '%';
        thumb.style.left = thumbPosition + '%';
        thumb.style.display = 'block';
      };

      // Update on scroll
      wrapper.addEventListener('scroll', updateScrollbar);

      // Update on window resize
      window.addEventListener('resize', updateScrollbar);

      // Drag thumb to scroll
      let isDraggingScrollbar = false;
      let startX;
      let startScrollLeft;

      thumb.addEventListener('mousedown', (e) => {
        isDraggingScrollbar = true;
        wrapper._scrollbarDragActive = true;
        startX = e.clientX;
        startScrollLeft = wrapper.scrollLeft;
        thumb.style.transition = 'none';
        e.preventDefault();
        e.stopPropagation();
      });

      document.addEventListener('mousemove', (e) => {
        if (!isDraggingScrollbar) return;

        const deltaX = e.clientX - startX;
        const scrollbarWidth = customScrollbar.offsetWidth;
        const scrollWidth = wrapper.scrollWidth;
        const scrollRatio = scrollWidth / scrollbarWidth;

        wrapper.scrollLeft = startScrollLeft + (deltaX * scrollRatio);
        e.preventDefault();
      });

      document.addEventListener('mouseup', () => {
        if (isDraggingScrollbar) {
          isDraggingScrollbar = false;
          wrapper._scrollbarDragActive = false;
          thumb.style.transition = '';

          // Pause auto-scroll after releasing scrollbar
          if (wrapper._pauseAutoScroll) {
            wrapper._pauseAutoScroll();
          }
        }
      });

      // Click on track to jump
      customScrollbar.addEventListener('click', (e) => {
        if (e.target === thumb) return;

        const rect = customScrollbar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const scrollbarWidth = customScrollbar.offsetWidth;
        const scrollWidth = wrapper.scrollWidth;
        const thumbWidth = thumb.offsetWidth;

        const targetScrollLeft = (clickX / scrollbarWidth) * scrollWidth - (wrapper.clientWidth / 2);
        wrapper.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });

        // Pause auto-scroll after clicking track
        if (wrapper._pauseAutoScroll) {
          wrapper._pauseAutoScroll();
        }
      });

      // Initial update
      updateScrollbar();
    });
  }

  /**
   * Auto-scroll functionality
   */
  initAutoScroll() {
    const sections = document.querySelectorAll('.featured-collection-home');

    sections.forEach(section => {
      const sectionElement = section.closest('[id^="shopify-section-"]');
      if (!sectionElement) return;

      // Get section ID to check settings
      const sectionId = sectionElement.id;
      const autoScrollEnabled = sectionElement.querySelector('[data-auto-scroll="true"]');

      // Only proceed if auto-scroll is enabled
      if (!autoScrollEnabled) return;

      const wrapper = section.querySelector('.featured-collection-home__scroll-wrapper');
      if (!wrapper) return;

      let scrollInterval;
      let isPaused = false;
      let resumeTimeout = null; // Timeout to resume auto-scroll after inactivity
      let scrollDirection = 1; // 1 for right, -1 for left
      const scrollSpeed = 1; // pixels per frame
      const scrollDelay = 30; // milliseconds between frames
      const inactivityDelay = 5000; // Resume auto-scroll after 5 seconds of inactivity
      let lastScrollLeft = wrapper.scrollLeft;
      let isAutoScrolling = false; // Track if currently auto-scrolling

      const startAutoScroll = () => {
        if (scrollInterval) return;

        scrollInterval = setInterval(() => {
          // Pause if user is hovering or interacting
          if (isPaused) return;

          isAutoScrolling = true;
          const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;

          // Scroll right
          if (scrollDirection === 1) {
            wrapper.scrollLeft += scrollSpeed;

            // Reverse when reaching the absolute end
            if (wrapper.scrollLeft >= maxScroll) {
              scrollDirection = -1;
            }
          }
          // Scroll left
          else {
            wrapper.scrollLeft -= scrollSpeed;

            // Reverse when reaching the absolute start
            if (wrapper.scrollLeft <= 0) {
              scrollDirection = 1;
            }
          }

          lastScrollLeft = wrapper.scrollLeft;
          isAutoScrolling = false;
        }, scrollDelay);
      };

      const stopAutoScroll = () => {
        if (scrollInterval) {
          clearInterval(scrollInterval);
          scrollInterval = null;
        }
      };

      const pauseAutoScroll = () => {
        isPaused = true;

        // Clear existing resume timeout
        if (resumeTimeout) {
          clearTimeout(resumeTimeout);
        }

        // Resume auto-scroll after inactivity delay
        resumeTimeout = setTimeout(() => {
          isPaused = false;
        }, inactivityDelay);
      };

      // Expose pauseAutoScroll to scrollbar
      wrapper._pauseAutoScroll = pauseAutoScroll;

      // Pause auto-scroll when cart drawer is open
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const drawerObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'class') {
              // Check if drawer is open (has 'active' class)
              const isOpen = cartDrawer.classList.contains('active');

              if (isOpen) {
                // Drawer opened - pause auto-scroll indefinitely
                isPaused = true;
                // Clear any pending resume timeout
                if (resumeTimeout) {
                  clearTimeout(resumeTimeout);
                  resumeTimeout = null;
                }
              } else {
                // Drawer closed - resume auto-scroll immediately
                isPaused = false;
              }
            }
          });
        });

        drawerObserver.observe(cartDrawer, {
          attributes: true,
          attributeFilter: ['class']
        });

        // Also check initial state
        if (cartDrawer.classList.contains('active')) {
          isPaused = true;
        }
      }

      // Detect manual scroll (drag or wheel)
      wrapper.addEventListener('scroll', () => {
        // Ignore scroll events caused by auto-scroll or scrollbar drag
        if (isAutoScrolling || wrapper._scrollbarDragActive) return;

        const currentScroll = wrapper.scrollLeft;
        const scrollDiff = Math.abs(currentScroll - lastScrollLeft);

        // If scroll change is larger than auto-scroll increment, it's manual
        if (scrollDiff > scrollSpeed + 1) {
          pauseAutoScroll();
        }

        lastScrollLeft = currentScroll;
      });

      // Pause on hover
      wrapper.addEventListener('mouseenter', () => {
        isPaused = true;
        // Clear resume timeout while hovering
        if (resumeTimeout) {
          clearTimeout(resumeTimeout);
        }
      });

      wrapper.addEventListener('mouseleave', () => {
        isPaused = false;
      });

      // Detect drag interaction
      let isDragging = false;
      wrapper.addEventListener('mousedown', () => {
        isDragging = true;
        isPaused = true;
        // Clear resume timeout during drag
        if (resumeTimeout) {
          clearTimeout(resumeTimeout);
        }
      });

      wrapper.addEventListener('mouseup', () => {
        if (isDragging) {
          isDragging = false;
          pauseAutoScroll(); // Resume after inactivity delay
        }
      });

      // Detect wheel scroll
      wrapper.addEventListener('wheel', (e) => {
        // Only pause if actually scrolling horizontally
        if (Math.abs(e.deltaX) > 0 || Math.abs(e.deltaY) > 0) {
          pauseAutoScroll();
        }
      });

      // Start auto-scroll
      startAutoScroll();

      // Stop on page unload
      window.addEventListener('beforeunload', stopAutoScroll);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FeaturedCollectionHomePage();
  });
} else {
  new FeaturedCollectionHomePage();
}
